import styles from './FJVideoRemoveBgControls.module.less';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useRef, useEffect, useState } from 'react';
import FJPlayIcon from '../../../../assets/svg/play.svg?react';
import FJPauseIcon from '../../../../assets/svg/stop.svg?react';
import { useCallback } from 'react';
import FJUtil from '../../../../util/FJUtil';
import { ID_LIST } from '../../../../redux/slices/FJSliceVideoBgRemover';

const FJVideoRemoveBgControls = ({
    active,
    seekVideo,
    totalTime,
    currentTime,
    isDisabled = false,
    play,
    pause,
    isPlaying,
    tracklets,
    setVideoThumbnailCanvas,
    updateVideoThumbnail,
    isDisabledKeyEvent = false,
}) => {
    const isMouseDown = useRef(false);
    const videoThumbnailCanvas = useRef(null);
    const thumbnailBoxRef = useRef(null);
    const [thumbnailRect, setThumbnailRect] = useState({
        width: 0,
        height: 0,
    });

    useEffect(() => {
        if (videoThumbnailCanvas.current) {
            setVideoThumbnailCanvas(videoThumbnailCanvas.current);
        }
    }, [setVideoThumbnailCanvas]);

    const handleSpacebar = useCallback(
        event => {
            if (event.code === 'Space' || event.code === 'KeyF') {
                if (isDisabled) return;

                if (isPlaying) {
                    pause();
                } else {
                    play();
                }
            }
        },
        [play, pause, isPlaying, isDisabled],
    );

    const handleMouseMove = useCallback(
        e => {
            if (isMouseDown.current) {
                const videoThumbnailContentRect = videoThumbnailCanvas.current.getBoundingClientRect();
                const offsetX = e.clientX - videoThumbnailContentRect.left;
                const percent = offsetX / videoThumbnailContentRect.width;
                seekVideo(Math.min(Math.max(0, percent * totalTime), totalTime));
            }
        },
        [seekVideo, totalTime],
    );

    const handleMouseUp = useCallback(() => {
        isMouseDown.current = false;

        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove]);

    const handleMouseDown = useCallback(
        e => {
            const videoThumbnailContentRect = videoThumbnailCanvas.current.getBoundingClientRect();
            const offsetX = e.clientX - videoThumbnailContentRect.left;
            const percent = offsetX / videoThumbnailContentRect.width;
            seekVideo(Math.min(Math.max(0, percent * totalTime), totalTime));
            isMouseDown.current = true;

            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        },
        [seekVideo, totalTime, handleMouseMove, handleMouseUp],
    );

    const handleVideoPlay = useCallback(() => {
        if (isPlaying) {
            pause();
        } else {
            play();
        }
    }, [play, pause, isPlaying]);

    const updateThumbnailRect = useCallback(() => {
        if (!thumbnailBoxRef.current) return;
        const thumbnailBox = thumbnailBoxRef.current.getBoundingClientRect();

        setThumbnailRect({
            width: thumbnailBox.width,
            height: thumbnailBox.height,
        });
    }, []);

    useEffect(() => {
        if (isDisabledKeyEvent) return;
        window.addEventListener('keydown', handleSpacebar);
        return () => {
            window.removeEventListener('keydown', handleSpacebar);
        };
    }, [handleVideoPlay, handleSpacebar, isDisabledKeyEvent]);

    useEffect(() => {
        const handleResize = () => {
            updateVideoThumbnail();
            updateThumbnailRect();
            pause();
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [updateVideoThumbnail, pause, updateThumbnailRect]);

    useEffect(() => {
        updateThumbnailRect();
    }, [updateThumbnailRect, active]);

    return (
        <div className={classNames(styles.container, { [styles.active]: active })} id={ID_LIST.REMOVE_BG_CONTROLS}>
            <div className={classNames(styles.container_left, { [styles.disabled]: isDisabled })}>
                <div className={styles.btn} onClick={handleVideoPlay}>
                    {isPlaying ? (
                        <FJPauseIcon className={styles.btn_play_icon} />
                    ) : (
                        <FJPlayIcon className={styles.btn_play_icon} />
                    )}
                </div>
            </div>
            <div
                className={classNames(styles.container_right, { [styles.disabled]: isDisabled })}
                onMouseDown={handleMouseDown}
                ref={thumbnailBoxRef}
            >
                <canvas className={styles.container_right_canvas} ref={videoThumbnailCanvas}></canvas>

                <div
                    className={styles.progress_line_box}
                    style={{ left: `${Math.round((currentTime / totalTime) * thumbnailRect.width)}px` }}
                >
                    <div className={styles.progress_line}></div>
                </div>

                {!isPlaying && (
                    <div className={styles.progress_time} style={{ left: `${(currentTime / totalTime) * 100}%` }}>
                        {new Date(currentTime * 1000).toISOString().substr(14, 5)}/
                        {new Date(totalTime * 1000).toISOString().substr(14, 5)}
                    </div>
                )}

                {tracklets.map(tracklet => {
                    if (tracklet.points.length === 0) {
                        return null;
                    }

                    return (
                        <div
                            key={tracklet.id}
                            className={classNames(styles.tracklet, {
                                [styles.active]: FJUtil.compareTimes(tracklet.currentTime, currentTime),
                            })}
                            style={{ left: `${(tracklet.currentTime / totalTime) * 100}%` }}
                            onClick={e => {
                                e.stopPropagation();
                                seekVideo(tracklet.currentTime);
                            }}
                            onMouseDown={e => {
                                e.stopPropagation();
                            }}
                        ></div>
                    );
                })}
            </div>
        </div>
    );
};

FJVideoRemoveBgControls.propTypes = {
    active: PropTypes.bool,
    seekVideo: PropTypes.func,
    totalTime: PropTypes.number,
    currentTime: PropTypes.number,
    setVideoThumbnailCanvas: PropTypes.func,
    play: PropTypes.func,
    isPlaying: PropTypes.bool,
    pause: PropTypes.func,
    tracklets: PropTypes.array,
    isDisabled: PropTypes.bool,
    updateVideoThumbnail: PropTypes.func,
    isDisabledKeyEvent: PropTypes.bool,
};

export default FJVideoRemoveBgControls;
